import { Can } from '@/domains/access-control';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { PageHeader } from '@/shared/components/PageHeader';
import { Pagination } from '@/shared/components/Pagination';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { AlertCircle, Clock, Eye, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CreateShiftInput, ShiftListItem } from '../types/shift';
import { ShiftsTableSkeleton } from './ShiftsTableSkeleton';

interface ShiftsViewProps {
  shifts?: ShiftListItem[];
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  search?: string;
  onSearchChange?: (search: string) => void;
  onPageChange?: (page: number) => void;
  onShiftClick?: (shiftId: string) => void;
  onCreateShift?: (data: CreateShiftInput) => Promise<void>;
  onDeleteShift?: (id: number) => Promise<void>;
}

const DEFAULT_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
];

export function ShiftsView({
  shifts = [],
  isLoading = false,
  isFetching = false,
  isError = false,
  totalCount = 0,
  page = 1,
  pageSize = 25,
  search = '',
  onSearchChange,
  onPageChange,
  onShiftClick,
  onCreateShift,
  onDeleteShift,
}: ShiftsViewProps) {
  // Local input mirrors the URL-owned search term; debounce pushes it up to the
  // parent so search runs server-side (matches Jobs/Users).
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newShiftTitle, setNewShiftTitle] = useState('');
  const [newShiftCode, setNewShiftCode] = useState('');
  const [newShiftStartTime, setNewShiftStartTime] = useState('');
  const [newShiftEndTime, setNewShiftEndTime] = useState('');
  const [newShiftColor, setNewShiftColor] = useState(DEFAULT_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Shift pending deletion — drives the confirmation dialog (null = closed).
  const [shiftToDelete, setShiftToDelete] = useState<ShiftListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sync external search changes (e.g. from the URL / back button) into the input.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Push the settled search term to the parent once debounced.
  useEffect(() => {
    if (debouncedSearch !== search) {
      onSearchChange?.(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // <input type="time"> produces zero-padded "HH:mm", so a string comparison
  // works as a chronological one. Equal times are also rejected.
  const hasTimeError =
    !!newShiftStartTime && !!newShiftEndTime && newShiftEndTime <= newShiftStartTime;

  const handleCreateShift = async () => {
    if (!newShiftTitle.trim() || !newShiftCode.trim() || !newShiftStartTime || !newShiftEndTime)
      return;
    if (hasTimeError) return;
    setIsSubmitting(true);
    try {
      await onCreateShift?.({
        title: newShiftTitle.trim(),
        ...(newShiftCode && { code: newShiftCode.trim() }),
        startTime: newShiftStartTime,
        endTime: newShiftEndTime,
        color: newShiftColor,
      });
      handleCloseCreateModal();
    } catch (error: any) {
      showErrorToast('Failed to Create Shift', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewShiftTitle('');
    setNewShiftCode('');
    setNewShiftStartTime('');
    setNewShiftEndTime('');
    setNewShiftColor(DEFAULT_COLORS[0]);
  };

  const handleConfirmDelete = async () => {
    if (!shiftToDelete) return;
    setIsDeleting(true);
    try {
      await onDeleteShift?.(shiftToDelete.id);
      setShiftToDelete(null);
    } catch {
      // Success/error toasts are handled by the caller's mutation.
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
          <p className="text-muted-foreground">Failed to load shifts</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Shift Management"
        subtitle={`${totalCount} shifts`}
        icon={<Clock className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
        actions={
          <Can action="create" subject="Shift">
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Shift
            </Button>
          </Can>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="bg-card/50 border border-border rounded-lg p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search shifts..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 bg-background border-border text-foreground"
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-medium">Colour</TableHead>
                <TableHead className="text-muted-foreground font-medium">Shift Title</TableHead>
                <TableHead className="text-muted-foreground font-medium">Code</TableHead>
                <TableHead className="text-muted-foreground font-medium">Start Time</TableHead>
                <TableHead className="text-muted-foreground font-medium">End Time</TableHead>
                <TableHead className="text-muted-foreground font-medium text-center">
                  Teams
                </TableHead>
                <TableHead className="text-muted-foreground font-medium text-center">
                  Users
                </TableHead>
                <TableHead className="text-muted-foreground font-medium text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching ? (
                // While a page/search loads, swap rows for skeletons sized to the
                // page so the table height (and pagination position) never jumps.
                <ShiftsTableSkeleton rows={pageSize} />
              ) : (
                shifts.map((shift) => (
                  <TableRow key={shift.id} className="border-border hover:bg-accent/50">
                    <TableCell>
                      <div className="w-10 h-5 rounded" style={{ backgroundColor: shift.color }} />
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-foreground">{shift.title}</div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{shift.code || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{shift.startTime}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{shift.endTime}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="outline"
                        className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                      >
                        {shift.assignedTeams}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="outline"
                        className="bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                      >
                        {shift.assignedUsers}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                          onClick={() => onShiftClick?.(String(shift.id))}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Can action="delete" subject="Shift">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => setShiftToDelete(shift)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {shifts.length === 0 && !isLoading && !isFetching && (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No shifts found</p>
            </div>
          </div>
        )}

        {totalCount > 0 && (
          <Pagination
            page={page}
            totalItems={totalCount}
            pageSize={pageSize}
            itemNoun="shifts"
            onPageChange={(p) => onPageChange?.(p)}
          />
        )}
      </div>

      {/* Create Shift Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Create New Shift</h2>
                <p className="text-sm text-muted-foreground">Define shift times and appearance</p>
              </div>
              <button
                onClick={handleCloseCreateModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Shift Title *
                </label>
                <Input
                  placeholder="e.g. Morning Shift"
                  value={newShiftTitle}
                  onChange={(e) => setNewShiftTitle(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Shift Code *
                </label>
                <Input
                  placeholder="e.g. AM"
                  value={newShiftCode}
                  onChange={(e) => setNewShiftCode(e.target.value)}
                />
              </div>

              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Start Time *
                    </label>
                    <Input
                      type="time"
                      value={newShiftStartTime}
                      onChange={(e) => setNewShiftStartTime(e.target.value)}
                      aria-invalid={hasTimeError}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      End Time *
                    </label>
                    <Input
                      type="time"
                      value={newShiftEndTime}
                      onChange={(e) => setNewShiftEndTime(e.target.value)}
                      aria-invalid={hasTimeError}
                    />
                  </div>
                </div>
                {hasTimeError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    End time must be after start time.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Colour</label>
                <div className="flex gap-2 flex-wrap">
                  {DEFAULT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        newShiftColor === color
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewShiftColor(color)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={handleCloseCreateModal}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleCreateShift}
                disabled={
                  !newShiftTitle.trim() ||
                  !newShiftCode.trim() ||
                  !newShiftStartTime ||
                  !newShiftEndTime ||
                  hasTimeError ||
                  isSubmitting
                }
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Shift
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={shiftToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setShiftToDelete(null);
        }}
        title="Delete Shift"
        description={
          shiftToDelete
            ? `"${shiftToDelete.title}" will be deleted. This action cannot be undone.`
            : undefined
        }
        confirmLabel="Delete Shift"
        isPending={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
